import { observable, action, Lambda, computed, observe } from "mobx";
import { DeviceDetect, eBranches } from "sb-resp-lib";
import { ISuperBannerItem, SiteDirection, CalcsProps, PaginationItemProps, SelectionType, Selection, PickedEvent, MasterEvent, Market, SuperBannerCMSConfiguration, ItemState, GameStatus, IClientIncludeInManager } from "../../../data";
import { DataParser } from "../../../parser";
import { OddsFormatHelper } from "../helpers";

// TODO
// slider step = card width + margin
const defaultStep = 304;

export class SuperBannerData {
    @observable currentSelectedSportId: number;
    @observable items: ISuperBannerItem[];
    oddsFormatter: OddsFormatHelper;
    calcs: CalcsProps;
    currentPage: number;
    @observable pages: PaginationItemProps[];
    @observable itemWidth: number | undefined;
    deviceDetect: DeviceDetect;
    disposeItemsChange: Lambda;
    disposeCurrentSelectedSportIdChange: Lambda;
    clientIncludeInManager:IClientIncludeInManager;

    get siteDirection(): SiteDirection {
        if (document.documentElement) {
            /* istanbul ignore next */
            return document.documentElement.getAttribute("dir") === "rtl"
                ? SiteDirection.rightToLeft
                : SiteDirection.leftToRight;
        }

        return SiteDirection.leftToRight;
    }

    @observable provider: DataParser;

    disposeList: Lambda[];
    private statusesToDisplay: ItemState[];

    constructor(provider: DataParser, oddsFormatHelper: OddsFormatHelper, clientIncludeInManager:IClientIncludeInManager ) {
        this.provider = provider;
        this.oddsFormatter = oddsFormatHelper;
        this.items = [];
        this.disposeList = [];
        this.clientIncludeInManager = clientIncludeInManager;

        this.items = this.provider.items;
        //this.reinitObserves();

        this.calcs = {
            step: defaultStep,
            container: 0,
            wrapper: 0,
            multiplier: 1
        };
        this.pages = [];
        this.currentPage = 0;
        this.itemWidth = undefined;
        this.deviceDetect = provider.dataFacade.deviceDetectHelper.deviceDetect;

        this.statusesToDisplay = [ItemState.available, ItemState.loading];
        this.currentSelectedSportId = provider.currentSelectedSportId;
        this.disposeItemsChange = observe(this.provider, (change) => {
            let changedObject = change.object as DataParser;
            this.updateCollection(changedObject);
        });
        this.disposeCurrentSelectedSportIdChange = observe(this.provider, "currentSelectedSportId", (change) => {
            this.updateCurrentSelectedSportId(change.newValue);
        })
    }

    @action updateCollection(change: DataParser): void {
        this.items = change.items;
    }

    @action.bound updateCurrentSelectedSportId(sportId: number): void {
        this.currentSelectedSportId = sportId;
    }

    @computed get itemsToDisplay() {

        return this.items.filter(item =>
            this.validateStatus(item) &&
            !(this.provider.dataFacade.cms.DisplayMobileMode && !item.PickedEvent)
            && this.isItemForCurrentSport(item)
            && this.isNotLiveRacingEvent(item));
    }

    @action.bound updateClientIncludeData(){
         this.items.forEach(item => item.ClientHashClass = this.clientIncludeInManager.getElementClass(item.ClientHashAttribute));
    }

    validateStatus(item: ISuperBannerItem): boolean {
        return this.statusesToDisplay.indexOf(item.state) !== -1 &&
            (!item.PickedEvent || this.statusesToDisplay.indexOf(item.PickedEvent.state) !== -1);
    }
    isItemForCurrentSport(item: ISuperBannerItem): boolean {
        return (this.currentSelectedSportId === item.SportId || item.SportId === 0);
    }

    isNotLiveRacingEvent(item: ISuperBannerItem): boolean {
        if (item.PickedEvent
            && item.PickedEvent.event
            && this.isRacingEvent(item.PickedEvent.event.sportID)
            && this.isLiveOrFinished(item.PickedEvent.event.status)) {
            return false;
        }
        return true;
    }

    private isRacingEvent(sportId: string): boolean {
        return sportId === eBranches.HorseRacing.toString()
            || sportId === eBranches.GreyHoundsRacing.toString();
    }

    private isLiveOrFinished(status: GameStatus): boolean {
        return status === GameStatus.RaceOff
            || status === GameStatus.Resulted;
    }

    // TODO Cover with tests
    /* istanbul ignore next */
    setupCalcs(wrapper: HTMLElement | null, container: HTMLElement | null, displayMultipleItems: boolean) {
        if (wrapper && container) {
            let items = container.getElementsByClassName("rj-superbanner__item");
            this.calcs.wrapper = wrapper.offsetWidth;
            !displayMultipleItems && this.setItemWidth(this.calcs.wrapper);
            this.calcs.container = container.offsetWidth;
            if (items.length) {
                let i = items[0]["offsetWidth"];
                let dI = i * items.length;
                let dC = (this.calcs.container - dI) / (items.length - 1);
                this.calcs.step = i + dC;
            }
        }
    }

    /* istanbul ignore next */
    @action.bound
    updatePagination() {
        let counter = this.getPagesCount(this.calcs);
        this.pages.length = counter;
        for (let i = 0; i < counter; i += 1) {
            this.pages[i] = {
                id: i,
                isCurrent: this.currentPage === i
            };
        }
    }

    /* istanbul ignore next */
    @action.bound
    updateCurrentPage(index: number) {
        this.pages = this.pages.map((page, i) => ({
            ...page,
            isCurrent: index === i
        }));
        this.currentPage = index;
        this.setSlideMultiplier(1);
    }

    /* istanbul ignore next */
    setCurrentPage(inc: number) {
        this.currentPage = Math.max(Math.min(this.currentPage + inc, this.pages.length - 1), 0);
    }

    /* istanbul ignore next */
    setSlideMultiplier(val: number) {
        this.calcs.multiplier = val;
    }

    /* istanbul ignore next */
    getInvisibleSpace(calcs: CalcsProps) {
        let invisibleSpace: number = 0;
        if (calcs && calcs.wrapper && calcs.container) {
            invisibleSpace = calcs.container - calcs.wrapper;
        }
        return Math.max(invisibleSpace, 0);
    }

    /* istanbul ignore next */
    getPagesCount(calcs: CalcsProps) {
        let counter = 0;
        if (calcs && calcs.step) {
            counter = Math.ceil(this.getInvisibleSpace(calcs) / calcs.step) + 1;
        }
        return counter;
    }

    /* istanbul ignore next */
    getProgressPageIndex(calcs: CalcsProps, progress: number) {
        return Math.max(
            Math.min(
                Math.round(-progress / calcs.step),
                this.getPagesCount(calcs) - 1),
            0);
    }

    /* istanbul ignore next */
    addToSlipTranspiler(selectionId: string, eventId: string): void {
        this.provider.addToSlipTranspiler(selectionId, eventId);
    }

    /* istanbul ignore next */
    getImageHeight(item: ISuperBannerItem, cms: SuperBannerCMSConfiguration): string {
        const event: PickedEvent | undefined = item.PickedEvent;

        if (!cms.DisplayMultipleItems && this.provider.dataFacade.deviceDetectHelper.deviceDetect.isDesktop) {

            return "220px";
        }

        if (event) {
            if (this.hasResolvedQAMarket(event) || this.hasPickedCMSMarketAndNotResolvedYetMarket(event)) {
                return "0px";
            }

            return "80px";
        }

        return "190px";
    }

    /* istanbul ignore next */
    private hasPickedCMSMarketAndNotResolvedYetMarket(event: PickedEvent): boolean {
        /* istanbul ignore next */
        return !event.market && event.IsOutright || event.IsQA;
    }

    /* istanbul ignore next */
    private hasResolvedQAMarket(event: PickedEvent): boolean {
        /* istanbul ignore next */
        return !!event.market && event.market.type === SelectionType.QA;
    }

    /* istanbul ignore next */
    getSuperBannerItemModifier(item: ISuperBannerItem): string {
        if (item.PickedEvent
            && item.PickedEvent.market
            && item.PickedEvent.market.type === SelectionType.QA) {
            return this.oddsFormatter.Outright.getOddsOrderClass(item.PickedEvent.market.selections);
        }

        return "";
    }

    /* istanbul ignore next */
    showFreeHtml(item: ISuperBannerItem): boolean {
        if (item.PickedEvent && item.PickedEvent.market && item.PickedEvent.market.type === SelectionType.QA) {
            return false;
        }

        return true;
    }

    /* istanbul ignore next */
    getBetBtnModifier(selection: Selection, type: SelectionType): string {
        const betBtnModifier = `rj-superbanner__bet-button UniqueOdd_${selection.sbtechId}_${type === SelectionType.QA ? 1 : 0}`;
        if (selection.isSelected) {
            return `${betBtnModifier} rj-superbanner__bet-button--selected chosenBet`;
        }

        return betBtnModifier;
    }

    /* istanbul ignore next */
    isSelectionInBetSlip(event: MasterEvent, market: Market, selection: Selection): boolean {
        return this.provider.isSelectionInBetSlip(event, market, selection);
    }

    @action.bound
    setItemWidth(w: number) {
        this.itemWidth = w;
    }

    eventHasStarted(ev: MasterEvent): boolean {
        return ev.isLive && ev.liveStartCountdown <= 0;
    }

    eventHasLiveScb(ev: MasterEvent): boolean {
        return !!ev.scoreboard && ev.isLive;
    }
}
